/**
 * React Query hooks for report data.
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks } from 'date-fns';
import { api } from '../lib/api';

// Report types
export type ReportType = 'project-status' | 'capacity' | 'time-tracking';

// Date range preset options
export type DateRangePreset = 'this-week' | 'last-week' | 'this-month' | 'custom';

/** Order summary for project status report */
export interface ProjectStatusOrder {
  gpSalesOrderNumber: string;
  customerName: string;
  promisedShipDate: string | null;
  expectedShipDate: string | null;
  status: string;
  totalTasks: number;
  completedTasks: number;
}

/** Project status report response */
export interface ProjectStatusReport {
  generatedAt: string;
  dateRange: { start: string; end: string };
  title: string;
  orders: ProjectStatusOrder[];
  summary: {
    totalOrders: number;
    onTrack: number;
    atRisk: number;
  };
}

/** Person capacity data */
export interface CapacityUtilization {
  name: string;
  role: string;
  totalMinutes: number;
  tasksWorked: number;
}

/** Capacity report response */
export interface CapacityReport {
  generatedAt: string;
  dateRange: { start: string; end: string };
  title: string;
  utilization: CapacityUtilization[];
}

/** Time entry for time tracking report */
export interface TimeTrackingEntry {
  id: string;
  personId: string;
  taskId: string;
  startTime: string;
  endTime: string | null;
  durationMinutes: number | null;
  personName: string;
  laborBucket: string;
  partNumber: string;
  gpSalesOrderNumber: string;
}

/** Time tracking report response */
export interface TimeTrackingReport {
  generatedAt: string;
  dateRange: { start: string; end: string };
  title: string;
  entries: TimeTrackingEntry[];
  summary: {
    totalEntries: number;
    totalHours: number;
  };
}

export type ReportData = ProjectStatusReport | CapacityReport | TimeTrackingReport;

interface ReportResponse<T> {
  data: T;
}

interface UseReportsOptions {
  reportType: ReportType;
  startDate: string;
  endDate: string;
  enabled?: boolean;
}

/**
 * Get date range based on preset selection.
 */
export function getDateRangeFromPreset(preset: DateRangePreset): { start: string; end: string } {
  const today = new Date();

  switch (preset) {
    case 'this-week': {
      const start = startOfWeek(today, { weekStartsOn: 1 }); // Monday
      const end = endOfWeek(today, { weekStartsOn: 1 }); // Sunday
      return {
        start: format(start, 'yyyy-MM-dd'),
        end: format(end, 'yyyy-MM-dd'),
      };
    }
    case 'last-week': {
      const lastWeekDate = subWeeks(today, 1);
      const start = startOfWeek(lastWeekDate, { weekStartsOn: 1 });
      const end = endOfWeek(lastWeekDate, { weekStartsOn: 1 });
      return {
        start: format(start, 'yyyy-MM-dd'),
        end: format(end, 'yyyy-MM-dd'),
      };
    }
    case 'this-month': {
      const start = startOfMonth(today);
      const end = endOfMonth(today);
      return {
        start: format(start, 'yyyy-MM-dd'),
        end: format(end, 'yyyy-MM-dd'),
      };
    }
    case 'custom':
    default: {
      // Default to current week
      const start = startOfWeek(today, { weekStartsOn: 1 });
      const end = endOfWeek(today, { weekStartsOn: 1 });
      return {
        start: format(start, 'yyyy-MM-dd'),
        end: format(end, 'yyyy-MM-dd'),
      };
    }
  }
}

/**
 * Get default date range (current week)
 */
export function getDefaultDateRange() {
  return getDateRangeFromPreset('this-week');
}

/**
 * Fetch report data for the specified type and date range.
 */
export function useReport(options: UseReportsOptions) {
  const { reportType, startDate, endDate, enabled = true } = options;

  return useQuery({
    queryKey: ['report', { reportType, startDate, endDate }],
    queryFn: async () => {
      const endpoint = `/reports/${reportType}`;
      const response = await api.get<ReportResponse<ReportData>>(endpoint, {
        params: { start: startDate, end: endDate },
      });

      // Defensive check - ensure data exists
      if (!response.data || typeof response.data !== 'object') {
        console.error('[useReport] Expected object but got:', typeof response.data, response.data);
        throw new Error('Invalid report data received');
      }

      return response.data;
    },
    enabled,
    staleTime: 30_000, // 30 seconds - reports data changes frequently
    refetchOnWindowFocus: false,
  });
}

interface ExportOptions {
  reportType: ReportType;
  format: 'json' | 'csv';
  dateRange: { start: string; end: string };
}

interface ExportResponse {
  r2Key: string;
  downloadUrl: string;
}

/**
 * Export report to R2 storage.
 */
export function useExportReport() {
  return useMutation({
    mutationFn: async (options: ExportOptions) => {
      return api.post<ExportResponse>('/reports/export', options);
    },
  });
}

/**
 * Convert report data to CSV format for client-side download.
 */
export function convertToCSV(data: ReportData): string {
  // Determine which data array to use based on report type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let rows: any[] = [];

  if ('orders' in data && data.orders) {
    rows = data.orders;
  } else if ('entries' in data && data.entries) {
    rows = data.entries;
  } else if ('utilization' in data && data.utilization) {
    rows = data.utilization;
  }

  if (rows.length === 0) return '';

  const firstRow = rows[0];
  if (!firstRow) return '';

  const headers = Object.keys(firstRow);
  const csv = [
    headers.join(','),
    ...rows.map((row) =>
      headers.map((h) => {
        const value = row[h];
        // Handle null/undefined and escape quotes
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        // Escape double quotes and wrap in quotes if contains comma
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      }).join(',')
    ),
  ].join('\n');

  return csv;
}

/**
 * Trigger client-side download of data.
 */
export function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Download report as JSON.
 */
export function downloadReportAsJSON(data: ReportData, reportType: ReportType): void {
  const content = JSON.stringify(data, null, 2);
  const filename = `${reportType}-report-${format(new Date(), 'yyyy-MM-dd-HHmm')}.json`;
  downloadFile(content, filename, 'application/json');
}

/**
 * Download report as CSV.
 */
export function downloadReportAsCSV(data: ReportData, reportType: ReportType): void {
  const content = convertToCSV(data);
  const filename = `${reportType}-report-${format(new Date(), 'yyyy-MM-dd-HHmm')}.csv`;
  downloadFile(content, filename, 'text/csv');
}

/**
 * Report type labels for UI display.
 */
export const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  'project-status': 'Project Status',
  'capacity': 'Capacity Utilization',
  'time-tracking': 'Time Tracking',
};

/**
 * Date preset labels for UI display.
 */
export const DATE_PRESET_LABELS: Record<DateRangePreset, string> = {
  'this-week': 'This Week',
  'last-week': 'Last Week',
  'this-month': 'This Month',
  'custom': 'Custom Range',
};
